import { useEffect, useState } from "react";
import { fetchLiveFlights, fetchAirlineStats, fetchBases } from "../api/opsApi";

// The airline as it is right now: who is airborne, and what has been flown over the last
// thirty days. Read once for the whole page - the hero figures and the map are two views of
// the same answer, so they must not poll separately.
//
// Both endpoints are cached on the server, so this polls on a plain interval without having to
// think about the vAMSYS rate limit.
const LIVE_EVERY_MS = 30 * 1000;
const STATS_EVERY_MS = 10 * 60 * 1000;

export default function useAirlineOps() {
  const [live, setLive] = useState(null);
  const [stats, setStats] = useState(null);
  // The bases do not move while anyone is looking, so they are read once.
  const [bases, setBases] = useState([]);

  useEffect(() => {
    let stopped = false;
    const controllers = new Set();

    const run = async (load, keep) => {
      if (document.hidden) return;
      const controller = new AbortController();
      controllers.add(controller);
      try {
        const value = await load({ signal: controller.signal });
        if (!stopped) keep(value);
      } catch {
        // A failed read leaves whatever is on screen. The next tick tries again.
      } finally {
        controllers.delete(controller);
      }
    };

    const readLive = () => run(fetchLiveFlights, setLive);
    const readStats = () => run(fetchAirlineStats, setStats);

    readLive();
    readStats();
    run(fetchBases, setBases);
    const liveTimer = window.setInterval(readLive, LIVE_EVERY_MS);
    const statsTimer = window.setInterval(readStats, STATS_EVERY_MS);
    // Back in front of the viewer, ask again rather than waiting out the interval.
    const onVisible = () => {
      if (!document.hidden) readLive();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stopped = true;
      window.clearInterval(liveTimer);
      window.clearInterval(statsTimer);
      document.removeEventListener("visibilitychange", onVisible);
      controllers.forEach((controller) => controller.abort());
    };
  }, []);

  return {
    airborne: live?.available ? live.count : null,
    flights: live?.flights ?? [],
    bases,
    stats,
  };
}
